/**
 * 1.5.13 §1c.5 — a deploy must not crash an open tab. After a deploy the old bundle asks for lazy chunks that no
 * longer exist ("Failed to fetch dynamically imported module", "Importing a module script failed", a
 * ChunkLoadError, or Vite's `vite:preloadError` for a stale script or CSS preload). The first time that happens
 * the page reloads by itself — the new version loads and the person carries on; a second failure straight after
 * the reload is a real fault and shows the error screen.
 *
 * The guard is a time in sessionStorage: one automatic reload per RELOAD_WINDOW_MS in this tab, so there is never a
 * reload loop, and a later deploy (the window long past) can reload again. If sessionStorage cannot be written the
 * page does not reload by itself at all — no guard, no automatic reload.
 */
export const RELOAD_KEY = 'abf.chunkReloadAt';
export const RELOAD_WINDOW_MS = 5 * 60 * 1000;

/** The one place the page reloads from (tests replace `now`). */
export const pageReload = {
  now(): void {
    window.location.reload();
  },
  /** 1.6.5: the same page at a cache-busting address, taking the place of this one (lib/app-update.ts). */
  to(url: string): void {
    window.location.replace(url);
  },
};

const CHUNK_WORDS = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Loading (?:CSS )?chunk [^\s]+ failed/i, // webpack-style runtimes
  /Unable to preload CSS/i, // Vite's CSS preload
  /Failed to load module script/i, // an HTML answer where a script was expected
];

export function isChunkLoadError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { name?: unknown; message?: unknown };
  if (err.name === 'ChunkLoadError') return true;
  const message = typeof err.message === 'string' ? err.message : '';
  return CHUNK_WORDS.some((w) => w.test(message));
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * This page has already asked for its one reload and is on its way out. One missing chunk is reported more than
 * once (the preload, then the import itself, then the screen that wanted it): all of them belong to that reload.
 * The flag lives as long as the page does — the reloaded page starts without it, and there the time in
 * sessionStorage decides.
 */
let leaving = false;

/** Tests only: the page "came back" from its reload. */
export function pageCameBack(): void {
  leaving = false;
}

/** May the page reload by itself now (or is it already doing so)? Read only — safe while rendering. */
export function canAutoReload(now: number = Date.now()): boolean {
  if (leaving) return true;
  const s = storage();
  if (!s) return false;
  try {
    const last = Number(s.getItem(RELOAD_KEY));
    return !(Number.isFinite(last) && last > 0 && now - last < RELOAD_WINDOW_MS);
  } catch {
    return false;
  }
}

/**
 * Reloads the page once for a chunk-load failure. False when it already did so a moment ago (or cannot remember
 * that it did): the caller then shows the error screen. True also while that one reload is under way.
 */
export function autoReloadOnce(now: number = Date.now()): boolean {
  if (leaving) return true;
  if (!canAutoReload(now)) return false;
  try {
    storage()!.setItem(RELOAD_KEY, String(now));
    // the write must have taken: a storage that silently drops it would mean a loop
    if (storage()!.getItem(RELOAD_KEY) !== String(now)) return false;
  } catch {
    return false;
  }
  leaving = true;
  pageReload.now();
  return true;
}

/**
 * Vite announces a failed preload of a lazy chunk (script or CSS) on `window` before the import rejects. Reload
 * once instead; when that is not allowed the event goes on, the import rejects and the error screen shows.
 */
export function installChunkReload(target: Window = window): () => void {
  const onPreloadError = (event: Event) => {
    if (autoReloadOnce()) event.preventDefault();
  };
  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
